import { useState, useMemo, useCallback } from 'react';
import { Receipt, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useGroups } from '@/hooks/useGroups';
import { useCategories } from '@/hooks/useCategories';
import { usePremium } from '@/hooks/usePremium';
import { useAllExpenses, useBatchDeleteExpenses, useBatchUpdateCategory } from '@/hooks/useExpenses';
import { useCommentCountsByExpenseIds } from '@/hooks/useComments';
import { useExpenseFilters } from '@/hooks/useExpenseFilters';
import { useDashboardRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { ExpenseFilters } from '@/components/expenses/ExpenseFilters';
import { ExpenseTable } from '@/components/expenses/ExpenseTable';
import { ExpenseCardList } from '@/components/expenses/ExpenseCardList';
import { ExpenseBatchBar } from '@/components/expenses/ExpenseBatchBar';
import { PremiumModal } from '@/components/common/PremiumModal';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { exportExpensesToCsv } from '@/lib/exportCsv';
import type { ExpenseWithGroup, AllExpensesFilters } from '@/api/expenses';
import { useTranslation } from 'react-i18next';

export function ExpensesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: groups } = useGroups();
  const { data: categories = [] } = useCategories();
  const { isPremium } = usePremium();
  const { filters, setFilters, clearFilters, hasActiveFilters } = useExpenseFilters();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const batchDelete = useBatchDeleteExpenses();
  const batchUpdateCategory = useBatchUpdateCategory();

  // Live updates when expenses are added/edited/deleted on another device.
  useDashboardRealtimeSubscription();

  // Scope to the user's non-archived groups (undefined while loading → query waits)
  const scopeGroupIds = useMemo(() => groups?.map(g => g.id), [groups]);
  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAllExpenses(filters, scopeGroupIds);

  // Flatten pages into a single array, apply "my expenses" client-side filter
  const allExpenses = useMemo(() => {
    if (!data) return [];
    const flat = data.pages.flatMap(page => page.expenses);
    if (!filters.myExpensesOnly || !user) return flat;
    return flat.filter(
      e => e.paid_by === user.id || e.splits?.some(s => s.user_id === user.id),
    );
  }, [data, filters.myExpensesOnly, user]);

  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Comment counts for loaded expenses
  const expenseIds = useMemo(() => allExpenses.map(e => e.id), [allExpenses]);
  const { data: commentCounts } = useCommentCountsByExpenseIds(expenseIds);

  // Summary: sum of loaded expenses in USD minor units
  const totalAmountUsd = useMemo(
    () => allExpenses.reduce((sum, e) => sum + Number(e.amount_minor_usd ?? 0), 0),
    [allExpenses],
  );

  // Group options for filter
  const groupOptions = useMemo(
    () => groups?.map(g => ({ id: g.id, name: g.name, color: g.color ?? '#6366F1' })),
    [groups],
  );

  // Selection handlers
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (allExpenses.every(e => selectedIds.has(e.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allExpenses.map(e => e.id)));
    }
  }, [allExpenses, selectedIds]);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(allExpenses.map(e => e.id)));
  }, [allExpenses]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Sort handler — compare effective values (defaults: date desc)
  const effectiveSortBy = filters.sortBy ?? 'date';
  const effectiveSortOrder = filters.sortOrder ?? 'desc';

  const handleSort = useCallback(
    (column: NonNullable<AllExpensesFilters['sortBy']>) => {
      if (effectiveSortBy === column) {
        setFilters({ sortOrder: effectiveSortOrder === 'asc' ? 'desc' : 'asc' });
      } else {
        setFilters({ sortBy: column, sortOrder: column === 'date' ? 'desc' : 'asc' });
      }
    },
    [effectiveSortBy, effectiveSortOrder, setFilters],
  );

  // Batch delete
  const handleBatchDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (!window.confirm(t('web:expensesPage.deleteConfirm', { count: ids.length }))) return;

    const result = await batchDelete.mutateAsync(ids);
    setSelectedIds(new Set());
    if (result.failed > 0) {
      alert(`Deleted ${result.deleted} of ${ids.length}. ${result.failed} failed (you can only delete expenses you paid).`);
    }
  }, [selectedIds, batchDelete]);

  // Batch export CSV
  const handleExportCsv = useCallback(() => {
    const selected = allExpenses.filter(e => selectedIds.has(e.id));
    exportExpensesToCsv(selected);
  }, [allExpenses, selectedIds]);

  // Batch change category
  const handleChangeCategory = useCallback(
    async (categoryId: string | null) => {
      const ids = Array.from(selectedIds);
      await batchUpdateCategory.mutateAsync({ ids, categoryId });
      setSelectedIds(new Set());
    },
    [selectedIds, batchUpdateCategory],
  );

  const hasSelection = selectedIds.size > 0;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">{t('common:tab.expenses')}</h1>
        {!isLoading && allExpenses.length > 0 && (
          <div className="flex items-center gap-3 text-sm text-text-tertiary">
            <span>{t('web:groupCard.expenses', { count: totalCount })}</span>
            <span>&middot;</span>
            <AmountDisplay
              amountMinor={totalAmountUsd}
              currency="USD"
              className="font-medium text-text-secondary"
            />
          </div>
        )}
      </div>

      {/* Filters */}
      <ExpenseFilters
        filters={filters}
        onFilterChange={setFilters}
        onClear={clearFilters}
        hasActiveFilters={hasActiveFilters}
        groups={groupOptions}
        categories={categories}
      />

      {/* Batch bar */}
      {hasSelection && (
        <ExpenseBatchBar
          selectedCount={selectedIds.size}
          totalCount={allExpenses.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onDelete={handleBatchDelete}
          onExportCsv={handleExportCsv}
          onChangeCategory={handleChangeCategory}
          categories={categories}
          isDeleting={batchDelete.isPending}
          isUpdating={batchUpdateCategory.isPending}
          premiumLocked={!isPremium}
          onPremiumBlocked={() => setShowPremiumModal(true)}
        />
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {t('web:expensesPage.failedToLoadExpenses', { message: error instanceof Error ? error.message : t('web:common.unknownError') })}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
        </div>
      )}

      {/* Desktop table (hidden on mobile) */}
      {!isLoading && allExpenses.length > 0 && (
        <>
          <div className="hidden md:block">
            <ExpenseTable
              expenses={allExpenses}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              sortBy={effectiveSortBy}
              sortOrder={effectiveSortOrder}
              onSort={handleSort}
              commentCounts={commentCounts}
            />
          </div>

          {/* Mobile card list */}
          <div className="md:hidden">
            <ExpenseCardList
              expenses={allExpenses}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              selectionMode={hasSelection}
              commentCounts={commentCounts}
            />
          </div>
        </>
      )}

      {/* Load more */}
      {hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="flex items-center gap-2 rounded-lg border border-border-secondary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover disabled:opacity-50"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common:loading')}
              </>
            ) : (
              t('web:common.loadMore')
            )}
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && allExpenses.length === 0 && (
        <div className="flex flex-col items-center rounded-xl bg-surface-primary p-12 shadow-sm ring-1 ring-border-primary text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
            <Receipt className="h-6 w-6 text-text-quaternary" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-text-primary">
            {hasActiveFilters ? t('expenses:empty.noMatchFilters') : t('expenses:empty.noExpensesYet')}
          </h3>
          <p className="mt-1 text-sm text-text-tertiary">
            {hasActiveFilters
              ? t('web:expensesPage.tryAdjustingYourFiltersOrClearing')
              : t('web:expensesPage.expensesWillAppearHereAsYou')}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-4 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              {t('expenses:empty.clearFilters')}
            </button>
          )}
        </div>
      )}

      <PremiumModal
        open={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        feature="CSV Export"
        description="Export your expenses to CSV with a Premium subscription."
      />
    </div>
  );
}
